import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register all Chart.js components
Chart.register(...registerables)

/**
 * Support Tickets Charts Controller
 *
 * Displays status distribution, activity trends, and category distribution charts
 */
export default class extends Controller {
  static values = {
    statusUrl: String,
    activityUrl: String,
    categoryUrl: String
  }

  static targets = [
    'statusChart',
    'activityChart',
    'categoryChart',
    'statusLoading',
    'activityLoading',
    'categoryLoading'
  ]

  connect() {
    this.statusChart = null
    this.activityChart = null
    this.categoryChart = null
    this.loadCharts()
  }

  disconnect() {
    if (this.statusChart) this.statusChart.destroy()
    if (this.activityChart) this.activityChart.destroy()
    if (this.categoryChart) this.categoryChart.destroy()
  }

  async loadCharts() {
    await Promise.all([this.loadStatusChart(), this.loadActivityChart(), this.loadCategoryChart()])
  }

  async loadStatusChart() {
    try {
      const response = await fetch(this.statusUrlValue)
      const data = await response.json()

      if (this.hasStatusLoadingTarget) {
        this.statusLoadingTarget.style.display = 'none'
      }
      if (this.hasStatusChartTarget) {
        this.statusChartTarget.style.display = 'block'
      }

      const ctx = this.statusChartTarget.getContext('2d')

      if (this.statusChart) {
        this.statusChart.destroy()
      }

      this.statusChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.data.map((d) => d.name),
          datasets: [
            {
              data: data.data.map((d) => d.value),
              backgroundColor: data.data.map((d) => d.color),
              borderWidth: 0,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 11 },
                color: '#6b7280',
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return chart.data.labels.map((label, i) => ({
                    text: `${label} (${datasets[0].data[i]})`,
                    fillStyle: datasets[0].backgroundColor[i],
                    strokeStyle: datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                    pointStyle: 'circle'
                  }))
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percentage = ((context.parsed / total) * 100).toFixed(1)
                  return `${context.label}: ${context.parsed} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    } catch (error) {
      console.error('[SupportTicketsCharts] Error loading status chart:', error)
      if (this.hasStatusLoadingTarget) {
        this.statusLoadingTarget.textContent = 'Failed to load chart'
        this.statusLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }

  async loadActivityChart() {
    try {
      const response = await fetch(this.activityUrlValue)
      const data = await response.json()

      if (this.hasActivityLoadingTarget) {
        this.activityLoadingTarget.style.display = 'none'
      }
      if (this.hasActivityChartTarget) {
        this.activityChartTarget.style.display = 'block'
      }

      const ctx = this.activityChartTarget.getContext('2d')

      if (this.activityChart) {
        this.activityChart.destroy()
      }

      this.activityChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.data.map((d) => d.date),
          datasets: [
            {
              label: 'New Tickets',
              data: data.data.map((d) => d.created),
              borderColor: 'rgb(239, 68, 68)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: 'rgb(239, 68, 68)',
              pointBorderColor: 'white',
              pointBorderWidth: 2
            },
            {
              label: 'Resolved',
              data: data.data.map((d) => d.resolved),
              borderColor: 'rgb(34, 197, 94)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: 'rgb(34, 197, 94)',
              pointBorderColor: 'white',
              pointBorderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              align: 'end',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 16,
                font: { size: 11 },
                color: '#6b7280'
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                title: (items) => {
                  const date = new Date(items[0].label)
                  return date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              ticks: {
                precision: 0,
                font: { size: 11 },
                color: '#6b7280'
              }
            },
            x: {
              type: 'time',
              time: {
                unit: 'day',
                displayFormats: {
                  day: 'MMM d'
                }
              },
              grid: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#6b7280',
                maxTicksLimit: 10
              }
            }
          },
          interaction: {
            mode: 'index',
            intersect: false
          }
        }
      })
    } catch (error) {
      console.error('[SupportTicketsCharts] Error loading activity chart:', error)
      if (this.hasActivityLoadingTarget) {
        this.activityLoadingTarget.textContent = 'Failed to load chart'
        this.activityLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }

  async loadCategoryChart() {
    try {
      const response = await fetch(this.categoryUrlValue)
      const data = await response.json()

      if (this.hasCategoryLoadingTarget) {
        this.categoryLoadingTarget.style.display = 'none'
      }
      if (this.hasCategoryChartTarget) {
        this.categoryChartTarget.style.display = 'block'
      }

      const ctx = this.categoryChartTarget.getContext('2d')

      if (this.categoryChart) {
        this.categoryChart.destroy()
      }

      this.categoryChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.data.map((d) => d.name),
          datasets: [
            {
              data: data.data.map((d) => d.value),
              backgroundColor: data.data.map((d) => d.color),
              borderWidth: 0,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 11 },
                color: '#6b7280',
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return chart.data.labels.map((label, i) => ({
                    text: `${label} (${datasets[0].data[i]})`,
                    fillStyle: datasets[0].backgroundColor[i],
                    strokeStyle: datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                    pointStyle: 'circle'
                  }))
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percentage = ((context.parsed / total) * 100).toFixed(1)
                  return `${context.label}: ${context.parsed} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    } catch (error) {
      console.error('[SupportTicketsCharts] Error loading category chart:', error)
      if (this.hasCategoryLoadingTarget) {
        this.categoryLoadingTarget.textContent = 'Failed to load chart'
        this.categoryLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }
}
